/**
 * bidParser.js — Pure utility functions for parsing spoken and typed bid
 * amounts.  No React, no side-effects — just string-in, number(s)-out.
 */

export const MAX_BID_AMOUNT = 150;

// ── Lookup tables ────────────────────────────────────────────────────

const NUMBER_WORDS = {
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
  seventy: 70,
  eighty: 80,
  ninety: 90,
};

const MAGNITUDE_WORDS = {
  hundred: 100,
  thousand: 1000,
};

// Words that map to single-digit values (0–9).  When two of these appear
// back-to-back in a transcript they almost certainly came from separate
// bidders shouting in quick succession, not someone saying a compound number.
const SINGLE_DIGIT_WORDS = new Set([
  "zero", "one", "two", "three", "four", "five",
  "six", "seven", "eight", "nine",
]);

// ── Patterns ─────────────────────────────────────────────────────────

// Detects repeated single digits that indicate overlapping bids or speech
// recognition stutter, e.g. "8 8" when two bidders speak at once. The digits
// must be separate tokens — spoken numbers like "eleven" arrive from the
// speech engine as "11" and must NOT be treated as a stutter.
export const REPEATED_DIGIT_PATTERN = /\b(\d)\s+\1\b/;
// Same idea for spoken number words: "six six", "eight eight", etc.
export const REPEATED_WORD_PATTERN =
  /\b(zero|one|two|three|four|five|six|seven|eight|nine)\s+\1\b/i;

// ── Helpers ──────────────────────────────────────────────────────────

export const normalizeTranscript = transcript =>
  transcript.toLowerCase().replace(/[^a-z0-9$\s-]/g, " ");

export const formatBidAmount = amount => `$${Number(amount).toLocaleString()}`;

// ── Number-word parser ───────────────────────────────────────────────

const parseNumberWords = tokens => {
  let current = 0;
  let total = 0;
  let found = false;

  for (const token of tokens) {
    if (token === "and" || token === "dollars" || token === "dollar") {
      continue;
    }

    if (NUMBER_WORDS[token] !== undefined) {
      current += NUMBER_WORDS[token];
      found = true;
      continue;
    }

    if (MAGNITUDE_WORDS[token] !== undefined) {
      if (current === 0) {
        current = 1;
      }
      current *= MAGNITUDE_WORDS[token];
      found = true;

      if (MAGNITUDE_WORDS[token] >= 1000) {
        total += current;
        current = 0;
      }
      continue;
    }

    if (found) {
      break;
    }
  }

  if (!found) {
    return null;
  }

  return total + current;
};

// ── Single-amount extraction ─────────────────────────────────────────

export const extractBidAmount = transcript => {
  const normalized = normalizeTranscript(transcript);
  const digitMatch = normalized.match(/\$?\d+(?:\.\d+)?/);

  if (digitMatch) {
    return Number.parseFloat(digitMatch[0].replace("$", ""));
  }

  const tokens = normalized.split(/\s+/).filter(Boolean);
  return parseNumberWords(tokens);
};

// ── Multi-bid splitting ──────────────────────────────────────────────

// Split a transcript into separate bid segments whenever consecutive
// single-digit numbers (words or digit characters) appear.
// e.g. "five six" → ["five", "six"]
//      "twenty five" → ["twenty five"]  (twenty is not a single digit)
//      "one hundred five six" → ["one hundred five", "six"]
const splitTranscriptIntoBids = normalized => {
  const tokens = normalized.split(/\s+/).filter(Boolean);

  const segments = [];
  let currentSegment = [];
  let lastWasSingleDigit = false;

  for (const token of tokens) {
    const isSingleDigit =
      SINGLE_DIGIT_WORDS.has(token) || /^\d$/.test(token);

    if (isSingleDigit && lastWasSingleDigit) {
      // Consecutive single digit — finalize the previous segment and start a new one.
      if (currentSegment.length > 0) {
        segments.push(currentSegment.join(" "));
      }
      currentSegment = [token];
    } else {
      currentSegment.push(token);
    }

    lastWasSingleDigit = isSingleDigit;
  }

  if (currentSegment.length > 0) {
    segments.push(currentSegment.join(" "));
  }

  return segments;
};

// Return an array of bid amounts extracted from a transcript.  Consecutive
// single-digit numbers are treated as separate bids rather than being
// combined into a single number.
export const extractAllBidAmounts = transcript => {
  const normalized = normalizeTranscript(transcript);
  const segments = splitTranscriptIntoBids(normalized);
  return segments
    .map(segment => extractBidAmount(segment))
    .filter(amount => amount !== null && !Number.isNaN(amount));
};

// ── Contextual bid resolution ────────────────────────────────────────

// Shorthand rule: once the bid is $20+, a single-digit bid replaces the
// ones digit of the current bid (current $37, "9" → $39).
export const applyBidShorthand = (amount, lastBid) => {
  if (lastBid !== null && lastBid >= 20 && amount >= 1 && amount <= 9) {
    return Math.floor(lastBid / 10) * 10 + amount;
  }
  return amount;
};

// Doubled-digit amounts (11, 22, … 99) are ambiguous: the speech engine
// transcribes one owner saying "forty-four" and two owners saying "four"
// at the same moment as the identical text "44".
const isDoubledDigitAmount = amount =>
  Number.isInteger(amount) && amount >= 11 && amount <= 99 && amount % 11 === 0;

/**
 * Resolve a raw extracted amount against the current bid.
 *
 * Returns the amount to record, or null when no valid raise exists.
 *
 * Doubled-digit amounts are resolved with auction context:
 *   - no current bid → take the literal amount (opening "22" is $22)
 *   - only one reading is a valid raise → take it
 *   - both readings valid → take the smaller (the collision-safe one, so
 *     two owners shouting "nine" at $28 yields $29, not $99)
 */
export const resolveBidAmount = (amount, lastBid) => {
  const isValidRaise = candidate =>
    candidate > 0 &&
    candidate <= MAX_BID_AMOUNT &&
    (lastBid === null || candidate > lastBid);

  const literal = applyBidShorthand(amount, lastBid);

  if (lastBid !== null && isDoubledDigitAmount(amount)) {
    const collapsed = applyBidShorthand(amount / 11, lastBid);
    const literalValid = isValidRaise(literal);
    const collapsedValid = isValidRaise(collapsed);
    if (literalValid && collapsedValid) return Math.min(literal, collapsed);
    if (literalValid) return literal;
    if (collapsedValid) return collapsed;
    return null;
  }

  return isValidRaise(literal) ? literal : null;
};

// ── Deduplication ────────────────────────────────────────────────────

// Collapse repeated single-digit numbers into one instance so overlapping
// bids like "six six" are treated as a single "six" (not rejected or doubled).
export const deduplicateRepeatedDigits = transcript => {
  let result = transcript;
  // Collapse repeated standalone digit tokens: "8 8" → "8".
  // Digits inside a single number ("11", "100") are left alone — those are
  // real amounts the speech engine produced from words like "eleven".
  result = result.replace(/\b(\d)\s+\1\b/g, "$1");
  // Collapse repeated single-digit words: "six six" → "six"
  result = result.replace(
    /\b(zero|one|two|three|four|five|six|seven|eight|nine)\s+\1\b/gi,
    "$1"
  );
  return result;
};
